import React, { useState, useEffect, useMemo } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { RRule, RRuleSet, rrulestr, Weekday } from 'rrule'; // Import rrule.js
import Datepicker from 'react-tailwindcss-datepicker';

// --- Configuration ---
const frequencies = [
  { value: 'monthly', label: 'Monthly', rruleFreq: RRule.MONTHLY, interval: 1, requires: 'dayOfMonth' },
  { value: 'biweekly', label: 'Bi-Weekly (Every 2 Weeks)', rruleFreq: RRule.WEEKLY, interval: 2, requires: 'dayOfWeek' },
  { value: 'quarterly', label: 'Quarterly', rruleFreq: RRule.MONTHLY, interval: 3, requires: 'dayOfMonth' },
  { value: 'semiannually', label: 'Semi-Annually', rruleFreq: RRule.MONTHLY, interval: 6, requires: 'dayOfMonth' },
  { value: 'yearly', label: 'Yearly', rruleFreq: RRule.YEARLY, interval: 1, requires: 'dayOfMonth' },
];

const MAX_DAY_OF_MONTH = 28;
const PREVIEW_COUNT = 5;

// Helper to get the RRule Weekday object (e.g., RRule.MO)
const getRruleWeekday = (jsDay) => {
  // JS Date: Sun=0, Mon=1, ..., Sat=6
  // RRule:   Mon=0, Tue=1, ..., Sun=6
  const rruleDayMap = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];
  return rruleDayMap[jsDay];
};

// Helper to find the next date matching the criteria
// Renamed from findNextStartDate to findAdjustedStartDate to reflect new logic
const findAdjustedStartDate = (startDate, freqConfig, dayOfMonth, rruleWeekday) => {
  console.log("[findAdjustedStartDate] Input startDate:", startDate);
  console.log("[findAdjustedStartDate] Input freqConfig:", freqConfig);
  console.log("[findAdjustedStartDate] Input dayOfMonth:", dayOfMonth);
  console.log("[findAdjustedStartDate] Input rruleWeekday:", rruleWeekday);

  const now = new Date();
  now.setUTCHours(0, 0, 0, 0); // Compare dates only

  // Check if input startDate is valid initially
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
    console.error("[findAdjustedStartDate] Invalid startDate passed to function:", startDate);
    // Decide how to handle this - maybe return null or throw an error?
    // For now, let's return null to indicate failure.
    return null;
  }

  let dt = new Date(startDate);
  dt.setUTCHours(0, 0, 0, 0);
  console.log("[findAdjustedStartDate] Initial dt (UTC):", dt.toISOString());

  // Adjust based on frequency requirements
  if (freqConfig.requires === 'dayOfMonth') {
    // Only adjust if the current day doesn't match
    // Note: We rely on the MAX_DAY_OF_MONTH validation elsewhere to prevent invalid dates (e.g., Feb 30)
    if (dt.getUTCDate() !== dayOfMonth) {
      dt.setUTCDate(dayOfMonth);
      console.log("[findAdjustedStartDate] dt after setUTCDate(dayOfMonth):", dt.toISOString());
    }
  } else { // dayOfWeek
    const targetJsDay = (rruleWeekday.weekday + 1) % 7; // Convert RRule day back to JS day (Sun=0...Sat=6)
    // Only adjust if the current day doesn't match the target weekday
    if (dt.getUTCDay() !== targetJsDay) {
      let safety = 0; // Prevent infinite loop
      // Advance day by day until the weekday matches
      while (dt.getUTCDay() !== targetJsDay && safety < 8) {
        dt.setUTCDate(dt.getUTCDate() + 1);
        safety++;
      }
      console.log("[findAdjustedStartDate] dt after weekday adjustment loop:", dt.toISOString(), "Safety iterations:", safety);
      if (safety >= 8) {
        console.error("[findAdjustedStartDate] Weekday adjustment loop safety break triggered.");
        return null; // Indicate failure
      }
    }
  }

  // --- Removed the tempRule and .after(now) logic ---
  // The goal is to return the user's chosen date, adjusted *only* for day/weekday rules,
  // not advanced based on the current date.

  // Final validation and return
  if (isNaN(dt.getTime())) {
    console.error("[findAdjustedStartDate] dt became invalid after adjustments!", dt);
    return null; // Indicate failure
  }
  console.log("[findAdjustedStartDate] Final adjusted dt:", dt.toISOString());
  return dt; // Return the adjusted date directly

};

// --- Component ---
const ScheduleSelector = ({
  label = 'Select Schedule Frequency & Start Date',
  fieldName = 'ical_rrule',
  initialFundRruleIcal,
  onIcalChange
}) => {

  // --- State ---
  const [selectedFrequencyValue, setSelectedFrequencyValue] = useState(frequencies[0].value);
  const [selectedDateValue, setSelectedDateValue] = useState(null); // e.g., { startDate: 'YYYY-MM-DD', endDate: '...' }
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [currentRruleIcal, setCurrentRruleIcal] = useState('');
  const [dateError, setDateError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // --- Memoized Values ---
  const selectedFrequencyConfig = useMemo(() =>
    frequencies.find(f => f.value === selectedFrequencyValue) || frequencies[0],
    [selectedFrequencyValue]
  );

  // --- Effects ---

  // Initialize from initialFundRruleIcal or set default
  useEffect(() => {
    let initialFreqVal = frequencies[0].value;
    let initialDate = null;
    let initialIcal = '';

    if (initialFundRruleIcal) {
      try {
        const rule = rrulestr(initialFundRruleIcal);
        const opts = rule instanceof RRuleSet ? rule.rrules()[0]?.options : rule.options;

        if (opts) {
          const freqMatch = frequencies.find(f => f.rruleFreq === opts.freq && f.interval === (opts.interval || 1));
          if (freqMatch) {
            initialFreqVal = freqMatch.value;
            const dtstart = opts.dtstart;
            let day = null;
            if (freqMatch.requires === 'dayOfMonth' && opts.bymonthday && opts.bymonthday.length > 0) {
              day = opts.bymonthday[0];
            } else if (freqMatch.requires === 'dayOfWeek' && opts.byweekday && opts.byweekday.length > 0) {
              // Need JS weekday from RRule weekday for initial display
              // RRule: MO=0..SU=6 , Date: SU=0..SA=6
              const jsDay = (opts.byweekday[0] + 1) % 7;
              day = jsDay; // We'll extract the weekday again later
            }

            if (dtstart && day !== null) {
              // Set the date picker to the dtstart date, ensuring the day aligns with rules
              const initialPickerDate = new Date(dtstart);
              initialPickerDate.setUTCHours(0, 0, 0, 0);
              // Validation check for initial load
              const initialDayOfMonth = initialPickerDate.getUTCDate();
              if (freqMatch.requires === 'dayOfMonth' && initialDayOfMonth > MAX_DAY_OF_MONTH) {
                setDateError(`Initial schedule day (${initialDayOfMonth}) is invalid (must be <= ${MAX_DAY_OF_MONTH}). Please select a new date.`);
                // Keep freq, but null date
              } else if (freqMatch.requires === 'dayOfWeek' && initialDayOfMonth > MAX_DAY_OF_MONTH && new Date(Date.UTC(initialPickerDate.getUTCFullYear(), initialPickerDate.getUTCMonth() + 1, 0)).getUTCDate() >= initialDayOfMonth) {
                // This case is tricky - if rule is biweekly Mon, but dtstart happens
                // to be Mon 30th, it's technically valid for rrule, but our UI constraint forbids it.
                // For simplicity, we enforce the <=28 constraint universally on the *picker*
                setDateError(`Initial schedule start day (${initialDayOfMonth}) is invalid (must be <= ${MAX_DAY_OF_MONTH}). Please select a new date.`);
                // Keep freq, but null date
              } else {
                initialDate = { startDate: initialPickerDate.toISOString().split('T')[0], endDate: initialPickerDate.toISOString().split('T')[0] };
                initialIcal = initialFundRruleIcal; // Keep original if valid
              }
            }
          }
        }
      } catch (e) {
        console.error("Error parsing initial RRULE string:", initialFundRruleIcal, e);
        setDateError("Could not parse initial schedule. Please select frequency and date.");
      }
    }

    // Set default date if none derived from initial prop
    if (!initialDate) {
      const nextMonth = new Date();
      nextMonth.setUTCHours(0, 0, 0, 0);
      nextMonth.setUTCDate(1); // Start with 1st
      nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
      initialDate = { startDate: nextMonth.toISOString().split('T')[0], endDate: nextMonth.toISOString().split('T')[0] };
    }

    setSelectedFrequencyValue(initialFreqVal);
    setSelectedDateValue(initialDate);
    setCurrentRruleIcal(initialIcal); // May be empty if default or invalid
    setIsInitialized(true);

  }, [initialFundRruleIcal]); // Run only once on mount/prop change

  // Recalculate RRULE, iCal string, and preview when frequency or date changes
  useEffect(() => {
    if (!isInitialized || !selectedDateValue || dateError) {
      // Don't calculate if initializing, no date selected, or date is invalid
      if (!dateError) {
        setUpcomingDates([]); // Clear preview if no date
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
      }
      return;
    }

    // --- Add Validation for selectedDateValue ---
    if (!selectedDateValue || !selectedDateValue.startDate) {
      console.error("[ScheduleSelector] useEffect - selectedDateValue or startDate is missing:", selectedDateValue);
      // Optionally clear state or set an error
      // setDateError("Please select a valid start date.");
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
      return; // Prevent further processing
    }
    // --- End Validation ---

    try {
      // Now it should be safer to create the Date object
      // --- Replace direct string concatenation with Date.UTC ---
      const [year, month, day] = selectedDateValue.startDate.split('-').map(Number);
      // Create date using Date.UTC (month is 0-indexed)
      const selectedTimestamp = Date.UTC(year, month - 1, day);
      const selectedDate = new Date(selectedTimestamp);
      // --- End Date.UTC change ---

      // --- Add validation for the created Date object ---
      if (isNaN(selectedDate.getTime())) {
        // This check might still be useful if Date.UTC somehow fails, though less likely
        console.error("[ScheduleSelector] useEffect - Failed to create valid Date from selectedDateValue:", selectedDateValue.startDate);
        setDateError("Invalid date selected. Please choose again.");
        setUpcomingDates([]);
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
        return;
      }
      // --- End Date object validation ---

      const dayOfMonth = selectedDate.getUTCDate(); // Use getUTCDate() as it's a UTC date
      const jsDayOfWeek = selectedDate.getUTCDay(); // Use getUTCDay()
      const rruleWeekday = getRruleWeekday(jsDayOfWeek);

      // Calculate the actual start date (next occurrence from today)
      // Pass the correctly parsed UTC date object
      // Use the new function to get the adjusted start date
      const dtstart = findAdjustedStartDate(
        selectedDate,
        selectedFrequencyConfig,
        dayOfMonth,
        rruleWeekday
      );

      // --- Debugging Logs ---
      console.log('[ScheduleSelector] Calculated dtstart:', dtstart);
      console.log('[ScheduleSelector] Type of dtstart:', typeof dtstart);
      let isValidDtstart = false;
      if (dtstart instanceof Date) {
        isValidDtstart = !isNaN(dtstart.getTime());
        console.log('[ScheduleSelector] Is dtstart valid Date?:', isValidDtstart);
        console.log('[ScheduleSelector] dtstart ISO String:', dtstart.toISOString());
      } else {
        console.log('[ScheduleSelector] dtstart is not a Date object.');
      }
      // --- End Debugging Logs ---

      // Ensure dtstart is valid before proceeding
      if (!isValidDtstart) {
        console.error("[ScheduleSelector] Calculated dtstart is invalid. Aborting rule generation.");
        setDateError("Internal error: Could not determine a valid start date.");
        setUpcomingDates([]);
        setCurrentRruleIcal('');
        if (onIcalChange) onIcalChange('');
        return; // Stop processing if dtstart is bad
      }

      // Build RRule options
      const rruleOptions = {
        freq: selectedFrequencyConfig.rruleFreq,
        interval: selectedFrequencyConfig.interval,
        dtstart: dtstart, // Use the validated dtstart
      };

      // Add frequency-specific rules (BYMONTHDAY, BYWEEKDAY, BYMONTH)
      if (selectedFrequencyConfig.rruleFreq === RRule.YEARLY) {
        // For yearly, we need both month and day
        const monthOfYear = selectedDate.getUTCMonth() + 1; // JS month is 0-indexed, RRule is 1-indexed
        rruleOptions.bymonth = [monthOfYear];
        rruleOptions.bymonthday = [dayOfMonth];
      } else if (selectedFrequencyConfig.requires === 'dayOfMonth') {
        // For other frequencies requiring day of month (Monthly, Quarterly, Semi-Annually)
        rruleOptions.bymonthday = [dayOfMonth];
      } else { // 'dayOfWeek' (Bi-Weekly)
        rruleOptions.byweekday = [rruleWeekday];
      }

      // Generate Rule and Preview
      // Wrap the RRule creation in its own try/catch for detailed logging
      let rule;
      try {
        console.log('[ScheduleSelector] Attempting to create RRule with options:', rruleOptions);
        rule = new RRule(rruleOptions);
        console.log('[ScheduleSelector] RRule created successfully.');
      } catch (ruleError) {
        console.error("[ScheduleSelector] Error creating main RRule object:", ruleError);
        console.error("[ScheduleSelector] Options passed to RRule:", rruleOptions);
        // Rethrow or handle as appropriate, maybe set the error state
        throw ruleError; // Rethrow to be caught by the outer try/catch
      }

      const newIcalString = rule.toString();
      const previewEndDate = new Date(dtstart);
      // Estimate end date for preview (e.g., ~1 year out)
      previewEndDate.setUTCFullYear(previewEndDate.getUTCFullYear() + 1);

      // Get the first PREVIEW_COUNT occurrences including dtstart
      const nextDates = rule.between(dtstart, previewEndDate, true).slice(0, PREVIEW_COUNT);

      // Update state
      setCurrentRruleIcal(newIcalString);
      setUpcomingDates(nextDates);

      // Notify parent
      if (onIcalChange) {
        onIcalChange(newIcalString);
      }

    } catch (e) {
      // Outer catch block remains
      console.error("[ScheduleSelector] Error generating RRULE (outer catch):", e);
      setDateError("Error calculating schedule. Please check selections.");
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
    }

    // Depend on initialized flag, error state, date, and frequency config
  }, [isInitialized, selectedFrequencyConfig, selectedDateValue, dateError, onIcalChange]);

  // --- Handlers ---
  const handleFrequencyChange = (newValue) => {
    setSelectedFrequencyValue(newValue);
  };

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.startDate) {
      setSelectedDateValue(null);
      setDateError(''); // Clear error if date is cleared
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
      return;
    }

    // Validate Date (Day <= MAX_DAY_OF_MONTH)
    const selectedDay = parseInt(newValue.startDate.split('-')[2], 10);
    if (selectedDay > MAX_DAY_OF_MONTH) {
      setDateError(`Selected day (${selectedDay}) is invalid. Please choose a day between 1 and ${MAX_DAY_OF_MONTH}.`);
      setSelectedDateValue(newValue); // Keep invalid value shown in picker, but error prevents rule generation
      setUpcomingDates([]);
      setCurrentRruleIcal('');
      if (onIcalChange) onIcalChange('');
    } else {
      setDateError(''); // Clear error on valid date
      setSelectedDateValue(newValue);
      // useEffect will handle recalculation
    }
  };

  // --- Render ---
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-500">Loading schedule...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <input type="hidden" name={fieldName} value={currentRruleIcal} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Frequency Selector (Listbox) */}
        <Listbox
          value={selectedFrequencyValue}
          onChange={handleFrequencyChange}
        >
          <div className="relative w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Billing Frequency</label>
            <ListboxButton className="relative w-full cursor-pointer rounded-lg bg-white py-2.5 pl-4 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm transition-all">
              <span className="block truncate font-medium">{selectedFrequencyConfig.label}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>

            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
            >
              {frequencies.map((option) => (
                <ListboxOption
                  key={option.value}
                  value={option.value}
                  className="group relative cursor-pointer select-none py-2.5 pl-4 pr-9 text-gray-900 data-[focus]:bg-blue-50 data-[focus]:text-blue-900"
                >
                  <span className="block truncate font-normal group-data-[selected]:font-semibold">
                    {option.label}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600 group-[&:not([data-selected])]:hidden">
                    <CheckIcon aria-hidden="true" className="h-5 w-5" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {/* Date Picker */}
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">First Due Date</label>
          <Datepicker
            useRange={false}
            asSingle={true}
            value={selectedDateValue}
            onChange={handleDateChange}
            placeholder={`Select ${selectedFrequencyConfig.requires === 'dayOfMonth' ? 'day (1-28)' : 'date'}`}
            inputClassName="relative w-full cursor-pointer rounded-lg bg-white py-2.5 pl-4 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm transition-all"
            containerClassName="relative"
            popoverDirection="down"
          />
          {dateError && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {dateError}
            </p>
          )}
        </div>
      </div>

      {/* Upcoming Dates Preview */}
      {upcomingDates.length > 0 && !dateError && (
        <div className="mt-5 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h4 className="text-sm font-semibold text-gray-900">Upcoming Due Dates</h4>
            <span className="text-xs text-gray-500 ml-auto">Next {upcomingDates.length} occurrences</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {upcomingDates.map((date, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg ${index === 0 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${index === 0 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {index + 1}
                </div>
                <span className={`text-sm ${index === 0 ? 'font-medium text-blue-900' : 'text-gray-700'}`}>
                  {date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                </span>
                {index === 0 && (
                  <span className="text-xs text-blue-600 ml-auto font-medium">Next</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleSelector;